'use client'
import { useParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { Configure, useInstantSearch } from 'react-instantsearch'

import { useIsHydrated } from '@/hooks/useIsHydrated'
import { AnalyticsEvent } from '@/lib/analytics/Analytics.constants'
import { buildCollectionPayload, gtm } from '@/lib/analytics/Analytics.utils'
import { decodeShopifyId } from '@/lib/shopify-id'

import { CollectionHeader } from './CollectionHeader'
import { CollectionPagination } from './CollectionPagination'
import { CollectionSkeleton } from './CollectionSkeleton'
import { PersistentFacetWidgets } from './PersistentFacetWidgets'
import { ProductsList } from './ProductsList'

import type { CategorySelectorType } from '@/types/contentful/categorySelectorType'

export function Collection({ categoryId }: CategorySelectorType) {
  const params = useParams()
  const isHydrated = useIsHydrated()
  const { results, status } = useInstantSearch()

  const isLoading = (status === 'loading' && !results?.hits?.length) || !isHydrated
  const collectionHandle = params?.collectionHandle as string
  const collectionId = decodeShopifyId(categoryId || '')

  const collectionFilter = useMemo(() => (collectionId ? `collection_ids:${collectionId}` : ''), [collectionId])

  useEffect(() => {
    if (results.hits.length === 0) return
    const payload = buildCollectionPayload({ items: results.hits, collectionId, collectionHandle: collectionHandle })
    gtm.push(AnalyticsEvent.COLLECTION_VIEWED, payload)
  }, [results.queryID, results.hits, collectionId, collectionHandle])

  return (
    // scroll-mt offsets the sticky header so pagination jumps land below it (see CollectionPagination).
    <section id="collection-products" data-name="collection-products" className="scroll-mt-20 bg-grey-100">
      <div className="mx-4 pt-6 sm:mx-12 sm:pt-12 lg:mx-[120px]">
        <Configure facets={['*']} filters={collectionFilter} distinct facetingAfterDistinct />
        <PersistentFacetWidgets />
        <CollectionHeader />
        {!isLoading ? <ProductsList collectionHandle={collectionHandle} collectionId={collectionId} /> : <CollectionSkeleton count={8} />}
        {!isLoading && <CollectionPagination />}
      </div>
    </section>
  )
}
